import isEqual from "lodash/isEqual"
import { useCallback } from "react"
import useLocalStorage from "./useLocalStorage"

export type MostRecentList<T> = {
  list: T[]
  index: number
}

export type UseMostRecentOptions = {
  /**
   * When true, selecting a value that already exists in the list only
   * updates the tracked index and does not reorder the list.
   */
  maintainOrder?: boolean
}

/**
 * Tracks a capped list of most-recently-used values in localStorage.
 *
 * Expects `initialValue` to be an array of default items. The length of that
 * array determines how many items are stored. The most recently stored item
 * is shifted to the front of the list (unless `maintainOrder` applies).
 */
function useMostRecent<T>(
  key: string,
  initialValue: T[],
  options: UseMostRecentOptions = {}
): [MostRecentList<T>, (value: T) => void] {
  const getValueStorage = useCallback(
    (value: T[] | T, index = 0): MostRecentList<T> =>
      Array.isArray(value)
        ? {
            list: value,
            index
          }
        : {
            list: initialValue,
            index: 0
          },
    [initialValue]
  )

  const [mostRecent, setMostRecent] = useLocalStorage<MostRecentList<T>>(
    key,
    getValueStorage(initialValue)
  )

  const recent: MostRecentList<T> =
    mostRecent?.list?.length
      ? mostRecent
      : getValueStorage(initialValue)

  const updateMostRecent = useCallback(
    (value: T) => {
      if (!key) return

      if (!Array.isArray(initialValue)) {
        setMostRecent(getValueStorage(value as T))
        return
      }

      // This ensures the most recent value only occurs once.
      const comparator = (v: T) => !isEqual(v, value)
      // Capping at current length.
      const maxOptions = recent.list.length

      // At what index to insert the new value.
      let insertIndex = 0
      let nextMostRecent = recent.list
      // Optionally maintain the order of the array if the value already
      // exists in the array.
      if (options.maintainOrder) {
        insertIndex = Math.max(
          insertIndex,
          recent.list.findIndex((v) => isEqual(v, value))
        )
      }

      // If value does not exist in the array, or if not maintaining order, or
      // if maintaining order but value exists in first position, then insert
      // value into first position. That last condition results in no change.
      if (insertIndex === 0) {
        nextMostRecent = [
          value,
          ...recent.list.filter(comparator)
        ].slice(0, maxOptions)
      }

      setMostRecent(
        getValueStorage(
          nextMostRecent,
          // Track the most recently used value by index.
          insertIndex === -1 ? 0 : insertIndex
        )
      )
    },
    [
      getValueStorage,
      initialValue,
      key,
      options.maintainOrder,
      recent.list,
      setMostRecent
    ]
  )

  return [recent, updateMostRecent]
}

export default useMostRecent
